import { useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Center,
    Checkbox,
    Divider,
    Flex,
    Group,
    Loader,
    LoadingOverlay,
    Modal,
    Stack,
    Switch,
    Text,
    Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDownload, IconGripVertical } from "@tabler/icons-react";
import dayjs from "dayjs";
import { type TFunction } from "i18next";
import {
    CampaignApplicationResolutionStatus,
    CampaignApplicationState,
    type Questionnaire,
    QuestionType,
} from "src/graphql/generated/graphql";
import { CampaignApplicationRestApi } from "src/types";
import { checkCountryCode, checkGender } from "src/utils/helpers";
import writeExcelFile, { Cell, SheetData } from "write-excel-file";

import { isWithdrawnApplication } from "../../components/CampaignApplicationsDataGrid/cell/role";
import { FormItem } from "../../components/FormItem";
import { CampaignQueryDataType, useQueryCampaignQuestionnaire } from "../../hooks/campaigns";
import { createModal } from "../../hooks/modals";
import { slugify } from "../../utils/slugify";

interface Context { }

interface Column {
    key: string;
    header: string;
    include: boolean;
    cell?: Partial<Cell>;
    format?: string;
    getValue(campaignApplication: CampaignApplicationRestApi, context: Context): string | number | Date;
}

export const getDefaultColumns = (t: TFunction, campaign: CampaignQueryDataType): Column[] => {
    return [
        {
            key: "id",
            header: t("campaignapplication_id"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.id;
            },
        },
        {
            key: "reviewer.id",
            header: t("reviewer_id"),
            include: false,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.id;
            },
        },
        {
            key: "campaignapplication.appliedat",
            header: t("campaignapplication_appliedat"),
            include: true,
            getValue(campaignApplication) {
                if (typeof campaignApplication?.appliedAt !== "number") {
                    return "-";
                }

                return dayjs(campaignApplication.appliedAt).toDate();
            },
        },
        {
            key: "reviewer.firstname",
            header: t("firstname"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.profile?.firstName;
            },
        },
        {
            key: "reviewer.lastname",
            header: t("lastname"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.profile?.lastName;
            },
        },
        {
            key: "role",
            header: t("campaignapplication_role"),
            include: true,
            getValue(campaignApplication) {
                // TTV2-1753: same words as the grid's role cell.
                if (isWithdrawnApplication(campaignApplication)) {
                    return t("campaignapplication_role_withdrawn");
                }

                switch (campaignApplication?.latestResolution?.status) {
                    case CampaignApplicationResolutionStatus.Selected:
                        return t("campaignapplication_role_selected");
                    case CampaignApplicationResolutionStatus.Alternate:
                        return t("campaignapplication_role_alternate");
                    case CampaignApplicationResolutionStatus.Rejected:
                        return campaignApplication?.isUnselected
                            ? t("campaignapplication_role_unselected")
                            : t("campaignapplication_role_rejected");
                }

                return "-";
            },
        },
        {
            key: "pendingrole",
            header: t("campaignapplication_pendingrole"),
            include: true,
            getValue(campaignApplication) {
                if (campaignApplication?.state !== CampaignApplicationState.Pending) {
                    return "-";
                }

                switch (campaignApplication?.pendingResolution?.status) {
                    case CampaignApplicationResolutionStatus.Selected:
                        return t("campaignapplication_role_selected");
                    case CampaignApplicationResolutionStatus.Alternate:
                        return t("campaignapplication_role_alternate");
                    case CampaignApplicationResolutionStatus.Rejected:
                        return t("campaignapplication_role_rejected");
                }

                return "-";
            },
        },
        {
            key: "reviewer.isBlacklisted",
            header: t("reviewer_blacklisted"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.isBlacklisted ? t("yes") : t("no");
            },
        },
        {
            key: "campaignapplication.discountcode",
            header: t("campaignapplication_discountcode"),
            include: true,
            getValue(campaignApplication) {
                if (campaignApplication?.discountCode) {
                    return campaignApplication?.discountCode;
                }

                return "-";
            },
        },
        {
            key: "campaignapplication.isdelivered",
            header: t("campaignapplication_isdelivered"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.isDelivered ? t("yes") : t("no");
            },
        },
        {
            key: "campaignapplication.deliveryconfirmedat",
            header: t("campaignapplication_deliveryconfirmedat"),
            include: true,
            getValue(campaignApplication) {
                if (campaignApplication?.deliveryConfirmedAt) {
                    return dayjs(campaignApplication.deliveryConfirmedAt).toDate();
                }

                return "-";
            },
        },
        {
            key: "campaignapplication.deadlineat",
            header: t("campaignapplication_deadlineat"),
            include: true,
            getValue(campaignApplication) {
                if (campaignApplication?.deadlineAt) {
                    return dayjs(campaignApplication.deadlineAt).toDate();
                }

                return "-";
            },
        },
        {
            key: "submittedreviewscount",
            header: t("reviews"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.submittedReviewsCount || 0;
            },
        },
        {
            key: "reviewer.gender",
            header: t("gender"),
            include: true,
            getValue(campaignApplication) {
                return checkGender(campaignApplication?.reviewer?.profile?.gender) ? t(`gender_${campaignApplication?.reviewer?.profile?.gender}`) : campaignApplication?.reviewer?.profile?.gender;
            },
        },
        {
            key: "reviewer.birthdate",
            header: t("birthdate"),
            include: true,
            getValue(campaignApplication) {
                if (typeof campaignApplication?.reviewer?.profile?.birthDate !== "number") {
                    return "-";
                }

                return dayjs(campaignApplication.reviewer.profile.birthDate).toDate();
            },
        },
        {
            key: "reviewer.age",
            header: t("age"),
            include: true,
            getValue(campaignApplication) {
                if (typeof campaignApplication?.reviewer?.profile?.age !== "number") {
                    return "-";
                }

                return campaignApplication.reviewer.profile.age;
            },
        },
        {
            key: "deliveryAddress.city",
            header: `${t("address_city")} [${t("deliveryaddress")}]`,
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.deliveryAddress?.city;
            },
        },
        {
            key: "deliveryAddress.country",
            header: `${t("address_country")} [${t("deliveryaddress")}]`,
            include: true,
            getValue(campaignApplication) {
                return checkCountryCode(campaignApplication?.deliveryAddress?.country) ? t(`country_${campaignApplication?.deliveryAddress?.country}`) : campaignApplication?.deliveryAddress?.country;
            },
        },
        {
            key: "deliveryAddress.postalcode",
            header: `${t("address_postalCode")} [${t("deliveryaddress")}]`,
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.deliveryAddress?.postalCode;
            },
        },
        {
            key: "reviewer.address.city",
            header: `${t("address_city")} [${t("homeaddress")}]`,
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.profile?.address?.city;
            },
        },
        {
            key: "reviewer.address.country",
            header: `${t("address_country")} [${t("homeaddress")}]`,
            include: true,
            getValue(campaignApplication) {
                return checkCountryCode(campaignApplication?.reviewer?.profile?.address?.country) ? t(`country_${campaignApplication?.reviewer?.profile?.address?.country}`) : campaignApplication?.reviewer?.profile?.address?.country;
            },
        },
        {
            key: "reviewer.address.postalcode",
            header: `${t("address_postalCode")} [${t("homeaddress")}]`,
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.profile?.address?.postalCode;
            },
        },
        {
            key: "reviewer.fbfollowers",
            header: `${t("reviewer_socials_followers_fb")}`,
            include: true,
            getValue(campaignApplication) {
                if (campaignApplication?.reviewer?.socials?.fbFollowers) {
                    return campaignApplication?.reviewer?.socials?.fbFollowers;
                }

                return "-";
            },
        },
        {
            key: "reviewer.igfollowers",
            header: `${t("reviewer_socials_followers_ig")}`,
            include: true,
            getValue(campaignApplication) {
                if (campaignApplication?.reviewer?.socials?.igFollowers) {
                    return campaignApplication?.reviewer?.socials?.igFollowers;
                }

                return "-";
            },
        },
        {
            key: "reviewer.totalApplicationsCount",
            header: t("reviewer_totalapplicationscount"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.totalApplicationsCount || 0;
            },
        },
        {
            key: "reviewer.selectedApplicationsCount",
            header: t("reviewer_selectedapplicationscount"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.selectedApplicationsCount || 0;
            },
        },
        {
            key: "reviewer.alternateApplicationsCount",
            header: t("reviewer_alternateapplicationscount"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.alternateApplicationsCount || 0;
            },
        },
        {
            key: "reviewer.rejectedApplicationsCount",
            header: t("reviewer_rejectedapplicationscount"),
            include: true,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.rejectedApplicationsCount || 0;
            },
        },
        {
            key: "reviewer.clientApplicationsCount",
            header: t("reviewer_clientapplicationscount"),
            include: !!campaign?.client?.id,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.clientCounts?.any || 0;
            },
        },
        {
            key: "reviewer.clientSelectedApplicationsCount",
            header: t("reviewer_clientselectedapplicationscount"),
            include: !!campaign?.client?.id,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.clientCounts?.selected || 0;
            },
        },
        {
            key: "reviewer.clientAlternateApplicationsCount",
            header: t("reviewer_clientalternateapplicationscount"),
            include: !!campaign?.client?.id,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.clientCounts?.alternate || 0;
            },
        },
        {
            key: "reviewer.clientRejectedApplicationsCount",
            header: t("reviewer_clientrejectedapplicationscount"),
            include: !!campaign?.client?.id,
            getValue(campaignApplication) {
                return campaignApplication?.reviewer?.clientCounts?.rejected || 0;
            },
        },
    ];
};

const getQuestionnaireColumns = (t: TFunction, questionnaire: Questionnaire): Column[] => {
    if (!questionnaire?.questions?.length) {
        return [];
    }

    const columns: Column[] = [];

    for (const question of questionnaire?.questions || []) {
        columns.push({
            key: `question.${question?.key}`,
            header: question?.text,
            include: true,
            // cell: { wrap: true },
            getValue(campaignApplication) {
                for (const answer of campaignApplication?.questionnaireResponse?.answers || []) {
                    if (answer.key === question?.key) {
                        const value = answer?.value || null;

                        switch (question?.type) {
                            case QuestionType.Radio:
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                return (value as any)?.text || "-";
                            case QuestionType.Checkbox:
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                return (
                                    (value as unknown as any[])?.map?.((option) => option?.text)?.join?.("\n") || "-"
                                );
                            case QuestionType.Text:
                            case QuestionType.Textarea:
                            case QuestionType.Number:
                                return value || "-";
                            case QuestionType.Date:
                            case QuestionType.Time:
                            case QuestionType.DateTime:
                                return "FIX ME";
                        }
                    }
                }

                return "-";
            },
        });
    }

    return columns;
};

export interface ExportCampaignApplicationsXLSXModalProps {
    campaign: CampaignQueryDataType;
    questionnaire?: Questionnaire;
    campaignApplications: CampaignApplicationRestApi[];
    onClose?();
}

export const ExportCampaignApplicationsXLSXModal = createModal<ExportCampaignApplicationsXLSXModalProps>(
    ({ campaign, questionnaire, campaignApplications, onClose }) => {
        const { t, i18n } = useTranslation(["common"]);

        const form = useForm({
            initialValues: {
                columns: getDefaultColumns(t, campaign),
                questionnaireColumns: getQuestionnaireColumns(t, questionnaire),
                includeRelatedCampaignsSelectedApplications: false,
                includeQuestionnaireResponse: !!questionnaire?.questions?.length,
            },
        });

        useEffect(() => {
            form.setValues({
                columns: getDefaultColumns(t, campaign),
                includeRelatedCampaignsSelectedApplications: !!form.values?.includeRelatedCampaignsSelectedApplications,
                includeQuestionnaireResponse: !!form.values?.includeQuestionnaireResponse,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [i18n?.language]);

        const handleToggleColumn = useCallback(
            (index: number) => {
                form.setFieldValue(`columns.${index}.include`, !(form.values?.columns || [])?.[index]?.include);
                // eslint-disable-next-line react-hooks/exhaustive-deps
            },
            [(form.values?.columns || []).map((column) => `${column?.key}${column?.include}`).join("")]
        );

        const handleToggleQuestionnaireColumn = useCallback(
            (index: number) => {
                form.setFieldValue(
                    `questionnaireColumns.${index}.include`,
                    !(form.values?.questionnaireColumns || [])?.[index]?.include
                );
                // eslint-disable-next-line react-hooks/exhaustive-deps
            },
            [(form.values?.questionnaireColumns || []).map((column) => `${column?.key}${column?.include}`).join("")]
        );

        const { queryCampaignQuestionnaire } = useQueryCampaignQuestionnaire();

        const [, setQuestionnaire] = useState<Questionnaire>(questionnaire);
        const [isDownloadingQuestionnaire, setIsDownloadingQuestionnaire] = useState(false);

        const downloadQuestionnaire = useCallback(async () => {
            setIsDownloadingQuestionnaire(true);

            const questionnaire = await queryCampaignQuestionnaire(campaign?.id);
            setQuestionnaire(questionnaire);
            form.setFieldValue("questionnaireColumns", getQuestionnaireColumns(t, questionnaire));

            setIsDownloadingQuestionnaire(false);
        }, [campaign?.id, form, queryCampaignQuestionnaire, t]);

        useEffect(() => {
            if (form.values?.includeQuestionnaireResponse && !questionnaire?.questions?.length) {
                downloadQuestionnaire();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [form.values?.includeQuestionnaireResponse]);

        const handleDownload = useCallback(async () => {
            const context: Context = {};

            const data: SheetData = [];

            data.push([]);
            data.push([
                {},
                {
                    value: t("exportedat"),
                    fontWeight: "bold",
                },
                {
                    value: dayjs().format("DD.MM.YYYY HH:mm:ss"),
                },
            ]);
            data.push([
                {},
                {
                    value: t("campaign"),
                    fontWeight: "bold",
                },
                {
                    value: campaign?.name,
                },
                {},
                {
                    value: t("client"),
                    fontWeight: "bold",
                },
                {
                    value: campaign?.client?.name,
                },
            ]);
            data.push([]);

            const headerRow: Cell[] = [];
            const columns = (form.values?.columns || []).filter((column) => column?.include);
            const questionnaireColumns = (form.values?.questionnaireColumns || []).filter((column) => column?.include);

            for (const column of columns) {
                headerRow.push({
                    value: column.header,
                    fontWeight: "bold",
                });
            }

            if (form.values?.includeQuestionnaireResponse) {
                for (const column of questionnaireColumns) {
                    headerRow.push({
                        value: column.header,
                        fontWeight: "bold",
                    });
                }
            }

            data.push(headerRow);

            for (const campaignApplication of campaignApplications || []) {
                const row: Cell[] = [];

                for (const column of columns) {
                    row.push({
                        ...(column?.cell || {}),
                        value: column.getValue(campaignApplication, context),
                    });
                }

                if (form.values?.includeQuestionnaireResponse) {
                    for (const column of questionnaireColumns) {
                        row.push({
                            ...(column?.cell || {}),
                            value: column.getValue(campaignApplication, context),
                        });
                    }
                }

                data.push(row);
            }

            await writeExcelFile([data], {
                dateFormat: "DD.MM.YYYY HH:mm:ss",
                sheets: [t("campaignapplications")],
                fileName: `campaign-applications_${slugify(campaign?.name)}_${dayjs().format("YYMMDDHHmmss")}.xlsx`,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [JSON.stringify(form.values)]);

        return (
            <Modal
                size="xl"
                title={<Title order={2}>{t("campaignapplication_bulk_downloadxlsx")}</Title>}
                onClose={onClose}
                opened
            >
                <LoadingOverlay
                    visible={isDownloadingQuestionnaire}
                    loader={
                        <Stack align="center" spacing="xs">
                            <Loader />
                            <Text>{t("campaignapplication_bulk_downloadxlsx_questionnaire_downloading")}</Text>
                        </Stack>
                    }
                />
                <Flex wrap="wrap">
                    <FormItem
                        label={t("campaignapplication_bulk_downloadxlsx_columns")}
                        style={{ width: "auto", minWidth: "270px" }}
                    >
                        <DragDropContext
                            onDragEnd={({ destination, source }) =>
                                form.reorderListItem("columns", { from: source.index, to: destination.index })
                            }
                        >
                            <Droppable droppableId="columns" direction="vertical">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {(form.values?.columns || []).map((column, index) => (
                                            <Draggable key={column?.key} draggableId={column?.key} index={index}>
                                                {(provided) => (
                                                    <Group
                                                        {...provided.draggableProps}
                                                        ref={provided.innerRef}
                                                        style={{ ...provided.draggableProps?.style }}
                                                        mt="xs"
                                                    >
                                                        <Center {...provided.dragHandleProps}>
                                                            <IconGripVertical size="1.2em" />
                                                        </Center>
                                                        <Checkbox
                                                            label={column?.header}
                                                            checked={column?.include}
                                                            onChange={() => handleToggleColumn(index)}
                                                        />
                                                    </Group>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </FormItem>
                    <Divider orientation="vertical" mx="lg" />
                    <Box style={{ flex: 1, minWidth: "270px" }}>
                        <FormItem label={t("campaignapplication_bulk_downloadxlsx_questionnaire")}>
                            <Switch
                                mt="sm"
                                label={t("campaignapplication_bulk_downloadxlsx_questionnaire_include")}
                                checked={form.values?.includeQuestionnaireResponse}
                                onChange={(event) =>
                                    form.setFieldValue("includeQuestionnaireResponse", event?.target?.checked)
                                }
                            />
                            {form.values?.includeQuestionnaireResponse && (
                                <FormItem
                                    mt="md"
                                    label={t("campaignapplication_bulk_downloadxlsx_questionnaire_columns")}
                                >
                                    <DragDropContext
                                        onDragEnd={({ destination, source }) =>
                                            form.reorderListItem("questionnaireColumns", {
                                                from: source.index,
                                                to: destination.index,
                                            })
                                        }
                                    >
                                        <Droppable droppableId="questionnaireColumns" direction="vertical">
                                            {(provided) => (
                                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                                    {(form.values?.questionnaireColumns || []).map((column, index) => (
                                                        <Draggable
                                                            key={column?.key}
                                                            draggableId={column?.key}
                                                            index={index}
                                                        >
                                                            {(provided) => (
                                                                <Group
                                                                    {...provided.draggableProps}
                                                                    ref={provided.innerRef}
                                                                    style={{ ...provided.draggableProps?.style }}
                                                                    align="center"
                                                                    mt="xs"
                                                                    noWrap
                                                                >
                                                                    <Center {...provided.dragHandleProps}>
                                                                        <IconGripVertical size="1.2em" />
                                                                    </Center>
                                                                    <Checkbox
                                                                        label={column?.header}
                                                                        checked={column?.include}
                                                                        onChange={() =>
                                                                            handleToggleQuestionnaireColumn(index)
                                                                        }
                                                                    />
                                                                </Group>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </FormItem>
                            )}
                        </FormItem>
                    </Box>
                </Flex>
                <Group mt="md" align="center" position="right">
                    <Button leftIcon={<IconDownload size="1em" />} onClick={handleDownload}>
                        {t("download")}
                    </Button>
                </Group>
            </Modal>
        );
    }
);
